import React from 'react';

import { RoundedButton } from '@/components/shared/RoundedButton';
import { EAccountDisplayState } from '@/components/AccountPanel/AccountPanel';
import { classNames } from '@/helpers/classNames';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';

import dashboardStyles from '../../Dashboard.module.scss';
import styles from './CompleteProfilePrompt.module.scss';

export const CompleteProfilePrompt: React.FC = () => {
	const { user } = useAuth();
	const { setAccountOpen } = useLayout();

	const [isDismissed, setIsDismissed] = React.useState(
		() => localStorage.getItem(`asaph_complete_profile_dismissed_${user.id}`) === 'true',
	);

	const aboutYouComplete = !!(user.gender && user.birthYear && user.color);
	const roleSkillsComplete = !!(
		user.role &&
		user.skills &&
		user.skills.length > 0
	);

	const fireProfileDismiss = useAnalyticsEvent('Dashboard_Complete_Profile_Dismiss');
	const fireOpenEdit = useAnalyticsEvent('Dashboard_Complete_Profile_Open_Edit');
	const fireOpenSkills = useAnalyticsEvent('Dashboard_Complete_Profile_Open_Skills');

	if (isDismissed || (aboutYouComplete && roleSkillsComplete)) return null;

	const handleDismiss = () => {
		fireProfileDismiss();
		localStorage.setItem(`asaph_complete_profile_dismissed_${user.id}`, 'true');
		setIsDismissed(true);
	};

	const openAboutYou = () => {
		fireOpenEdit();
		setAccountOpen({
			open: true,
			defaultDisplayState: EAccountDisplayState.EDIT_PROFILE,
		});
	};

	const openRoleSkills = () => {
		fireOpenSkills();
		setAccountOpen({
			open: true,
			defaultDisplayState: EAccountDisplayState.SERVING,
		});
	};

	const showAboutYou = !aboutYouComplete;
	const showRoleSkills = !roleSkillsComplete;

	const aboutYouMissing = [
		!user.gender && 'gender',
		!user.birthYear && 'birth year',
		!user.color && 'avatar color',
	].filter(Boolean) as string[];

	const roleSkillsMissing = [
		!user.role && 'role',
		(!user.skills || user.skills.length === 0) && 'skills',
	].filter(Boolean) as string[];

	return (
		<div className={styles['root']}>
			<div className={styles['header']}>
				<div className={dashboardStyles['title']}>Complete your profile</div>
				<RoundedButton color="ghost" onClick={handleDismiss}>Dismiss</RoundedButton>
			</div>
			<div className={styles['items']}>
				{showAboutYou && (
					<CompleteProfileItem
						key="about-you"
						number={1}
						title={`Add your ${aboutYouMissing.join(', ')}`}
						subtitle="Personalize Asaph and improve insights"
						isActive
						isLast={!showRoleSkills}
						buttonComponent={
							<RoundedButton onClick={openAboutYou}>Edit Profile</RoundedButton>
						}
					/>
				)}
				{showRoleSkills && (
					<CompleteProfileItem
						key="role-skills"
						number={showAboutYou ? 2 : 1}
						title={`Add your ${roleSkillsMissing.join(', ')}`}
						subtitle="Improve collaboration and reporting"
						isActive={!showAboutYou}
						isLast
						buttonComponent={
							<RoundedButton onClick={openRoleSkills}>Add Skills</RoundedButton>
						}
					/>
				)}
			</div>
		</div>
	);
};

interface CompleteProfileItemProps {
	number: number;
	isLast?: boolean;
	title: string;
	subtitle: string;
	isActive?: boolean;
	buttonComponent: React.ReactNode;
}

const CompleteProfileItem: React.FC<CompleteProfileItemProps> = ({
	number,
	title,
	subtitle,
	isActive,
	buttonComponent,
	isLast,
}) => {
	return (
		<div
			className={classNames([
				styles['item'],
				isActive ? styles['active'] : '',
				isLast ? styles['last'] : '',
			])}
		>
			<div className={styles['timeline']}>
				<div className={styles['number']}>{number}</div>
				{!isLast ? <div className={styles['line']} /> : null}
			</div>
			<div className={styles['content']}>
				<div className={styles['title']}>{title}</div>
				<div className={styles['subtitle']}>{subtitle}</div>
			</div>
			<div className={styles['actions']}>{buttonComponent}</div>
		</div>
	);
};
